import React, { useState, useEffect } from 'react';
import './AnnouncementBoard.css';

function AnnouncementBoard({ currentUser }) {
  const [announcements, setAnnouncements] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    message: '',
    priority: 'normal'
  });

  useEffect(() => {
    loadAnnouncements();
  }, []);

  const loadAnnouncements = () => {
    const saved = localStorage.getItem('nitroAnnouncements');
    if (saved) {
      setAnnouncements(JSON.parse(saved));
    } else {
      // Initialize with sample announcements
      const initial = [
        {
          id: 1,
          title: 'Welcome to NitroDrop!',
          message: 'Welcome to our giveaway platform! Make sure to join our Discord server for updates and exclusive giveaways.',
          priority: 'high',
          author: 'System',
          createdAt: new Date().toISOString(),
          expiresAt: null
        }
      ];
      setAnnouncements(initial);
      localStorage.setItem('nitroAnnouncements', JSON.stringify(initial));
    }
  };

  const canCreateAnnouncement = () => {
    const allowedRoles = ['founder', 'co-founder', 'manager', 'administrator', 'head-admin', 'admin', 'mod'];
    return currentUser && allowedRoles.includes(currentUser.role);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!canCreateAnnouncement()) return;

    const newAnnouncement = {
      id: Date.now(),
      title: formData.title,
      message: formData.message,
      priority: formData.priority,
      author: currentUser.discordUsername,
      role: currentUser.role,
      createdAt: new Date().toISOString(),
      expiresAt: null
    };

    const updated = [newAnnouncement, ...announcements];
    setAnnouncements(updated);
    localStorage.setItem('nitroAnnouncements', JSON.stringify(updated));
    
    setFormData({ title: '', message: '', priority: 'normal' });
    setShowForm(false);
  };

  const handleDelete = (id) => {
    if (!canCreateAnnouncement()) return;
    const updated = announcements.filter(a => a.id !== id);
    setAnnouncements(updated);
    localStorage.setItem('nitroAnnouncements', JSON.stringify(updated));
  };

  const getPriorityClass = (priority) => {
    switch (priority) {
      case 'high': return 'priority-high';
      case 'urgent': return 'priority-urgent';
      default: return 'priority-normal';
    }
  };

  const filteredAnnouncements = announcements.filter(announcement => {
    if (!announcement.expiresAt) return true;
    return new Date(announcement.expiresAt) > new Date();
  });

  return (
    <div className="announcement-board">
      <div className="board-header">
        <h2 className="section-title">📢 Announcement Board</h2>
        {canCreateAnnouncement() && (
          <button className="btn-primary" onClick={() => setShowForm(!showForm)}>
            {showForm ? 'Cancel' : '+ New Announcement'}
          </button>
        )}
      </div>

      {showForm && canCreateAnnouncement() && (
        <form className="announcement-form" onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Title *</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              required
              placeholder="Announcement title"
            />
          </div>
          <div className="form-group">
            <label>Message *</label>
            <textarea
              value={formData.message}
              onChange={(e) => setFormData({ ...formData, message: e.target.value })}
              required
              placeholder="Announcement message"
              rows="4"
            />
          </div>
          <div className="form-group">
            <label>Priority</label>
            <select
              value={formData.priority}
              onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
            >
              <option value="normal">Normal</option>
              <option value="high">High</option>
              <option value="urgent">Urgent</option>
            </select>
          </div>
          <button type="submit" className="submit-btn">Post Announcement</button>
        </form>
      )}

      <div className="announcements-list">
        {filteredAnnouncements.length === 0 ? (
          <div className="no-announcements">
            <p>No announcements yet. Check back soon!</p>
          </div>
        ) : (
          filteredAnnouncements.map(announcement => (
            <div key={announcement.id} className={`announcement-card ${getPriorityClass(announcement.priority)}`}>
              <div className="announcement-header">
                <div className="announcement-meta">
                  <h3>{announcement.title}</h3>
                  <div className="announcement-author">
                    <span className="author-name">{announcement.author}</span>
                    {announcement.role && (
                      <span className={`role-badge role-${announcement.role}`}>
                        {announcement.role}
                      </span>
                    )}
                    <span className="announcement-date">
                      {new Date(announcement.createdAt).toLocaleDateString()}
                    </span>
                  </div>
                </div>
                {canCreateAnnouncement() && (
                  <button 
                    className="delete-btn"
                    onClick={() => handleDelete(announcement.id)}
                    title="Delete announcement"
                  >
                    ×
                  </button>
                )}
              </div>
              <div className="announcement-message">
                {announcement.message}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default AnnouncementBoard;

